import supabase from "@/lib/supabase";

import type { OnboardingToestand } from "@/domain/onboarding/onboardingGate";

const EDGE_NAME = "legal-acceptance";

export type LegalDocumentView = {
  key?: "TOS" | "DPA";
  title: string;
  version: string;
  documentDate: string;
  path: string;
};

export type CurrentLegalDocuments = {
  accepted: LegalDocumentView[];
  informational: LegalDocumentView[];
};

export type AcceptancePayload = {
  legalName: string;
  enterpriseNumber: string;
  addressLine1: string;
  addressPostalCode: string;
  addressCity: string;
  addressCountryCode: string;
  contactEmail: string;
  incidentContactName: string;
  incidentContactEmail: string;
  contractMode: "pilot" | "paying";
  tosVersion: string;
  dpaVersion: string;
};

export type AcceptanceResultaat =
  | { soort: "ok"; acceptanceId: string }
  /** De getoonde documentversie is verouderd — de pagina moet herladen. */
  | { soort: "versiedrift"; detail: string }
  | { soort: "fout"; detail: string };

/**
 * Haalt de status van de eigen tenant op. Eén rij door RLS; `maybeSingle` zou
 * bij meer dan één rij stilzwijgend falen, dus we lezen expliciet de eerste.
 */
/** Wie er kijkt, en vanuit welke stoel. Bepaalt of onboarding überhaupt van toepassing is. */
export type OnboardingActor = {
  role: string | null | undefined;
  /** De thuis-tenant uit het gebruikersprofiel. */
  tenantId: string | null | undefined;
  /** De tenant waarin een platformbeheerder werkt; null = zuivere platformcontext. */
  platformEffectiveTenantId: string | null | undefined;
};

export async function fetchOnboardingStatus(
  actor: OnboardingActor | null,
): Promise<OnboardingToestand> {
  /*
   * EERST DE CONTEXT, DAN PAS DE TENANTRIJ.
   *
   * Een platformbeheerder zonder gekozen tenantcontext handelt niet namens een klant.
   * Zonder deze stap las de gate de tenantrij van zijn THUIS-tenant — na de reset van
   * 09-09-2026 de dossiervrije provisioningmaster — en eiste klantonboarding vóór
   * Platformbeheer bereikbaar was.
   *
   * De vrijstelling hangt aan rol ÉN afwezige context. Een beheerder die wél een tenant
   * heeft gekozen, werkt namens die tenant en valt gewoon onder diens onboarding.
   *
   * De actor komt van de AANROEPER en wordt hier niet opnieuw opgehaald: de gate keyt
   * zijn query erop, zodat het wisselen van tenantcontext vanzelf een nieuwe bevraging
   * oplevert. Zou deze functie de actor zelf lezen, dan bleef een gecachet oordeel uit de
   * vorige context staan — en precies dát liet de eerste versie een tenant zonder
   * afgeronde onboarding gewoon doorlaten.
   */
  if (
    actor != null &&
    actor.role === "super_admin" &&
    (actor.platformEffectiveTenantId == null || actor.platformEffectiveTenantId === "")
  ) {
    return { soort: "platformcontext" };
  }

  /*
   * DE TENANT WORDT EXPLICIET GENOEMD, NIET AAN RLS OVERGELATEN.
   *
   * Hier stond `.select(...).limit(1)` zonder filter, in de veronderstelling dat RLS
   * precies de eigen tenantrij teruggeeft. Voor een gewone gebruiker klopt dat
   * (`tenants_tenant_isolation_select`), maar `tenants_super_admin_select` laat een
   * platformbeheerder ALLE tenants lezen — `limit(1)` gaf hem dus een WILLEKEURIGE
   * tenant. Gemeten op 09-09-2026: in tenantcontext op de demo (onboarding niet
   * afgerond) liet de gate gewoon door, omdat de eerste rij toevallig een tenant mét
   * afgeronde onboarding was. De gate was daarmee voor beheerders zinloos in beide
   * richtingen.
   */
  const doelTenant = actor?.platformEffectiveTenantId ?? actor?.tenantId ?? null;
  if (doelTenant == null) {
    // Zonder bekende tenant toont de gate een herstelpagina.
    return { soort: "onbekend" };
  }

  const { data, error } = await supabase
    .from("tenants")
    .select("onboarding_completed_at,customer_acceptance_required,legal_request_required,legal_clearance_at")
    .eq("id", doelTenant)
    .maybeSingle();

  if (error || !data) {
    // Geen oordeel: de gate houdt toegang tegen en biedt opnieuw controleren aan.
    return { soort: "onbekend" };
  }
  const rij = data as { onboarding_completed_at: string | null; customer_acceptance_required?: boolean; legal_request_required?:boolean;legal_clearance_at?:string|null };
  if(rij.legal_request_required===true&&!rij.legal_clearance_at)return {soort:"legal-open"};
  if (rij.customer_acceptance_required === false) return { soort: "bestaande-toegang" };
  return { soort: "bekend", voltooidOp: rij.onboarding_completed_at ?? null };
}

export async function fetchCurrentLegalDocuments(): Promise<CurrentLegalDocuments> {
  const { data, error } = await supabase.functions.invoke<CurrentLegalDocuments>(EDGE_NAME, {
    body: { op: "current" },
  });
  if (error || !data) {
    throw new Error(error?.message ?? "documenten konden niet worden opgehaald");
  }
  return data;
}

/** Leest status en foutdetail uit een mislukte `functions.invoke`. */
async function leesFout(error: unknown): Promise<{ status: number | null; detail: string }> {
  const context = (error as { context?: unknown } | null)?.context;
  if (context instanceof Response) {
    let detail = "";
    try {
      const body = (await context.clone().json()) as { detail?: string; error?: string };
      detail = body.detail ?? body.error ?? "";
    } catch {
      detail = "";
    }
    return { status: context.status, detail };
  }
  return { status: null, detail: (error as { message?: string } | null)?.message ?? "onbekende fout" };
}

export async function submitLegalAcceptance(
  payload: AcceptancePayload,
): Promise<AcceptanceResultaat> {
  const { data, error } = await supabase.functions.invoke<{ acceptanceId: string }>(EDGE_NAME, {
    body: { op: "accept", accepted: true, ...payload },
  });

  if (error) {
    const { status, detail } = await leesFout(error);
    if (status === 409) {
      return {
        soort: "versiedrift",
        detail: detail || "de getoonde documentversie is verouderd — herlaad de pagina",
      };
    }
    return { soort: "fout", detail: detail || "de aanvaarding kon niet worden vastgelegd" };
  }

  if (!data?.acceptanceId) {
    return { soort: "fout", detail: "de aanvaarding kon niet worden vastgelegd" };
  }
  return { soort: "ok", acceptanceId: data.acceptanceId };
}
